import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import {
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogContent,
  DialogActions,
  Button,
  Dropdown,
  Option,
  Textarea,
  Input,
  Radio,
  RadioGroup,
  ProgressBar,
  Badge,
  Spinner,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import {
  MailRegular,
  ChatRegular,
  SendRegular,
  CheckmarkCircleRegular,
  DismissCircleRegular,
  ChevronLeftRegular,
  ChevronRightRegular,
  PersonRegular,
  BuildingRegular,
  PhoneRegular,
} from '@fluentui/react-icons';
import {
  sendOutreach,
  sendSmsOutreach,
  getEmailTemplates,
  type EligibleClient,
  type EmailTemplate,
  type EmailTemplateClientType,
  type SendOutreachRequest,
} from '../../services/api';
import { OutreachChannel, SendSmsOutreachRequest } from '../../types';

interface SendOutreachDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedClients: EligibleClient[];
  taxYear: number;
  onComplete?: () => void;
}

type WizardStep = 'channel' | 'compose' | 'preview' | 'sending' | 'complete';

const useStyles = makeStyles({
  stepIndicator: {
    display: 'flex',
    justifyContent: 'center',
    gap: '8px',
    marginBottom: '24px',
  },
  step: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  stepNumber: {
    width: '24px',
    height: '24px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '12px',
    fontWeight: '600',
    backgroundColor: tokens.colorNeutralBackground4,
    color: tokens.colorNeutralForeground3,
  },
  stepNumberActive: {
    backgroundColor: tokens.colorBrandBackground,
    color: tokens.colorNeutralForegroundOnBrand,
  },
  stepNumberComplete: {
    backgroundColor: tokens.colorPaletteGreenBackground2,
    color: tokens.colorPaletteGreenForeground1,
  },
  stepLabel: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
  },
  stepLabelActive: {
    color: tokens.colorBrandForeground1,
    fontWeight: tokens.fontWeightSemibold,
  },
  stepConnector: {
    width: '40px',
    height: '2px',
    backgroundColor: tokens.colorNeutralStroke2,
    margin: '0 8px',
  },
  stepConnectorComplete: {
    backgroundColor: tokens.colorPaletteGreenBackground2,
  },
  channelOption: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '12px',
    padding: '16px',
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: '8px',
    cursor: 'pointer',
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground2Hover,
    },
  },
  channelOptionSelected: {
    border: '1px solid #0078d4',
    backgroundColor: '#deecf9',
  },
  channelIcon: {
    fontSize: '24px',
    color: tokens.colorBrandForeground1,
  },
  channelContent: {
    flex: 1,
  },
  channelTitle: {
    fontWeight: tokens.fontWeightSemibold,
    marginBottom: '4px',
  },
  channelDescription: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
  },
  formField: {
    marginBottom: '16px',
  },
  fieldLabel: {
    display: 'block',
    marginBottom: '4px',
    fontWeight: tokens.fontWeightSemibold,
  },
  charCount: {
    fontSize: tokens.fontSizeBase100,
    color: tokens.colorNeutralForeground3,
    textAlign: 'right',
    marginTop: '4px',
  },
  charCountWarning: {
    color: tokens.colorPaletteYellowForeground1,
  },
  charCountError: {
    color: tokens.colorPaletteRedForeground1,
  },
  previewCard: {
    padding: '16px',
    backgroundColor: tokens.colorNeutralBackground2,
    borderRadius: '8px',
    marginBottom: '12px',
  },
  previewClient: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '8px',
  },
  previewContent: {
    padding: '12px',
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: '4px',
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    maxHeight: '200px',
    overflow: 'auto',
  },
  sendingProgress: {
    textAlign: 'center',
    padding: '40px',
  },
  resultSummary: {
    padding: '16px',
    borderRadius: '8px',
    marginBottom: '16px',
  },
  resultSuccess: {
    backgroundColor: tokens.colorPaletteGreenBackground1,
    border: `1px solid ${tokens.colorPaletteGreenBorder1}`,
  },
  resultError: {
    backgroundColor: tokens.colorPaletteRedBackground1,
    border: `1px solid ${tokens.colorPaletteRedBorder1}`,
  },
  resultMixed: {
    backgroundColor: tokens.colorPaletteYellowBackground1,
    border: `1px solid ${tokens.colorPaletteYellowBorder1}`,
  },
  clientList: {
    maxHeight: '200px',
    overflow: 'auto',
  },
  clientItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px',
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
});

const SMS_MAX_LENGTH = 160;
const SMS_WARNING_LENGTH = 140;

export default function SendOutreachDialog({
  open,
  onOpenChange,
  selectedClients,
  taxYear,
  onComplete,
}: SendOutreachDialogProps) {
  const styles = useStyles();
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  // Wizard state
  const [step, setStep] = useState<WizardStep>('channel');
  const [channel, setChannel] = useState<OutreachChannel>('email');

  // Email state
  const [selectedTemplate, setSelectedTemplate] = useState<string>('tax_season_kickoff');
  const [customSubject, setCustomSubject] = useState('');
  const [customBody, setCustomBody] = useState('');

  // SMS state
  const [smsMessage, setSmsMessage] = useState('');

  // Preview state
  const [previewIndex, setPreviewIndex] = useState(0);

  // Send results
  const [sendProgress, setSendProgress] = useState(0);
  const [sendResults, setSendResults] = useState<{
    emailSuccess: number;
    emailFailed: number;
    smsSuccess: number;
    smsFailed: number;
    errors: string[];
  } | null>(null);

  // Fetch email templates
  const { data: templates } = useQuery<EmailTemplate[]>({
    queryKey: ['emailTemplates'],
    queryFn: () => getEmailTemplates(),
  });

  // Clients with valid email/phone
  const clientsWithEmail = useMemo(() =>
    selectedClients.filter(c => c.email),
    [selectedClients]
  );
  const clientsWithPhone = useMemo(() =>
    selectedClients.filter(c => c.phone),
    [selectedClients]
  );

  // Count individual vs business clients
  const clientTypeCounts = useMemo(() => {
    const individuals = selectedClients.filter(c => c.clientType === 'contact').length;
    const businesses = selectedClients.filter(c => c.clientType === 'account').length;
    return { individuals, businesses, isMixed: individuals > 0 && businesses > 0 };
  }, [selectedClients]);

  // Group templates by client type for display
  const groupedTemplates = useMemo(() => {
    if (!templates) return { individual: [], business: [], auto: [], all: [] };

    const groups: Record<EmailTemplateClientType, EmailTemplate[]> = {
      individual: [],
      business: [],
      auto: [],
      all: [],
    };

    templates.forEach(tpl => {
      groups[tpl.clientType || 'all'].push(tpl);
    });

    return groups;
  }, [templates]);

  // Email mutation
  const emailMutation = useMutation({
    mutationFn: (request: SendOutreachRequest) => sendOutreach(request),
  });

  // SMS mutation
  const smsMutation = useMutation({
    mutationFn: (request: SendSmsOutreachRequest) => sendSmsOutreach(request),
  });

  const resetDialog = () => {
    setStep('channel');
    setChannel('email');
    setSelectedTemplate('tax_season_kickoff');
    setCustomSubject('');
    setCustomBody('');
    setSmsMessage('');
    setPreviewIndex(0);
    setSendProgress(0);
    setSendResults(null);
  };

  const handleClose = () => {
    resetDialog();
    onOpenChange(false);
  };

  const handleComplete = () => {
    queryClient.invalidateQueries({ queryKey: ['eligibleClients'] });
    queryClient.invalidateQueries({ queryKey: ['outreachHistory'] });
    onComplete?.();
    handleClose();
  };

  const handleSend = async () => {
    setStep('sending');
    setSendProgress(0);

    const results = {
      emailSuccess: 0,
      emailFailed: 0,
      smsSuccess: 0,
      smsFailed: 0,
      errors: [] as string[],
    };

    // Send emails
    if ((channel === 'email' || channel === 'both') && clientsWithEmail.length > 0) {
      try {
        const template = templates?.find(tpl => tpl.id === selectedTemplate);
        const emailResult = await emailMutation.mutateAsync({
          clientIds: clientsWithEmail.map(c => c.id),
          taxYear,
          subject: customSubject || template?.subject.replace('{year}', (taxYear + 1).toString()),
          template: selectedTemplate,
          customBody: selectedTemplate === 'custom' ? customBody : undefined,
          includeDocumentChecklist: template?.includesDocumentChecklist,
          includeQuestionnaire: template?.includesQuestionnaire,
        });
        results.emailSuccess = emailResult.successCount;
        results.emailFailed = emailResult.failCount;
        emailResult.results
          .filter(r => !r.success)
          .forEach(r => results.errors.push(t('sendOutreachDialog.errors.emailToClient', { name: r.clientName, error: r.error })));
      } catch (error) {
        results.emailFailed = clientsWithEmail.length;
        results.errors.push(t('sendOutreachDialog.errors.emailError', { message: error instanceof Error ? error.message : t('sendOutreachDialog.errors.unknownError') }));
      }
    }

    setSendProgress(50);

    // Send SMS
    if ((channel === 'sms' || channel === 'both') && clientsWithPhone.length > 0) {
      try {
        const smsResult = await smsMutation.mutateAsync({
          clientIds: clientsWithPhone.map(c => c.id),
          taxYear,
          message: smsMessage,
        });
        results.smsSuccess = smsResult.successCount;
        results.smsFailed = smsResult.failCount;
        smsResult.results
          .filter(r => !r.success)
          .forEach(r => results.errors.push(t('sendOutreachDialog.errors.smsToClient', { name: r.clientName, error: r.error })));
      } catch (error) {
        results.smsFailed = clientsWithPhone.length;
        results.errors.push(t('sendOutreachDialog.errors.smsError', { message: error instanceof Error ? error.message : t('sendOutreachDialog.errors.unknownError') }));
      }
    }

    setSendProgress(100);
    setSendResults(results);
    setStep('complete');
  };

  const canProceed = () => {
    switch (step) {
      case 'channel':
        return true;
      case 'compose':
        if (channel === 'email' || channel === 'both') {
          if (selectedTemplate === 'custom' && !customBody.trim()) return false;
        }
        if (channel === 'sms' || channel === 'both') {
          if (!smsMessage.trim() || smsMessage.length > SMS_MAX_LENGTH) return false;
        }
        return true;
      case 'preview':
        return true;
      default:
        return false;
    }
  };

  const handleNext = () => {
    switch (step) {
      case 'channel':
        setStep('compose');
        break;
      case 'compose':
        setPreviewIndex(0);
        setStep('preview');
        break;
      case 'preview':
        handleSend();
        break;
    }
  };

  const handleBack = () => {
    switch (step) {
      case 'compose':
        setStep('channel');
        break;
      case 'preview':
        setStep('compose');
        break;
    }
  };

  const getStepIndex = () => {
    switch (step) {
      case 'channel': return 0;
      case 'compose': return 1;
      case 'preview': return 2;
      case 'sending':
      case 'complete': return 3;
    }
  };

  const currentPreviewClient = selectedClients[previewIndex];

  const renderStepIndicator = () => {
    const steps = [
      t('sendOutreachDialog.steps.channel'),
      t('sendOutreachDialog.steps.compose'),
      t('sendOutreachDialog.steps.preview'),
      t('sendOutreachDialog.steps.send'),
    ];
    const currentIndex = getStepIndex();

    return (
      <div className={styles.stepIndicator}>
        {steps.map((label, index) => (
          <div key={label} className={styles.step}>
            <div
              className={`${styles.stepNumber} ${
                index < currentIndex ? styles.stepNumberComplete :
                index === currentIndex ? styles.stepNumberActive : ''
              }`}
            >
              {index < currentIndex ? <CheckmarkCircleRegular /> : index + 1}
            </div>
            <span
              className={`${styles.stepLabel} ${
                index === currentIndex ? styles.stepLabelActive : ''
              }`}
            >
              {label}
            </span>
            {index < steps.length - 1 && (
              <div
                className={`${styles.stepConnector} ${
                  index < currentIndex ? styles.stepConnectorComplete : ''
                }`}
              />
            )}
          </div>
        ))}
      </div>
    );
  };

  const renderChannelStep = () => (
    <div>
      <RadioGroup value={channel} onChange={(_, data) => setChannel(data.value as OutreachChannel)}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <label
            className={`${styles.channelOption} ${channel === 'email' ? styles.channelOptionSelected : ''}`}
            onClick={() => setChannel('email')}
          >
            <Radio value="email" />
            <MailRegular className={styles.channelIcon} />
            <div className={styles.channelContent}>
              <div className={styles.channelTitle}>{t('sendOutreachDialog.channel.emailOnlyTitle')}</div>
              <div className={styles.channelDescription}>
                {t('sendOutreachDialog.channel.emailOnlyDescription', { count: clientsWithEmail.length })}
              </div>
            </div>
          </label>

          <label
            className={`${styles.channelOption} ${channel === 'sms' ? styles.channelOptionSelected : ''}`}
            onClick={() => setChannel('sms')}
          >
            <Radio value="sms" />
            <ChatRegular className={styles.channelIcon} />
            <div className={styles.channelContent}>
              <div className={styles.channelTitle}>{t('sendOutreachDialog.channel.smsOnlyTitle')}</div>
              <div className={styles.channelDescription}>
                {t('sendOutreachDialog.channel.smsOnlyDescription', { count: clientsWithPhone.length })}
              </div>
            </div>
          </label>

          <label
            className={`${styles.channelOption} ${channel === 'both' ? styles.channelOptionSelected : ''}`}
            onClick={() => setChannel('both')}
          >
            <Radio value="both" />
            <div style={{ display: 'flex', gap: '4px' }}>
              <MailRegular className={styles.channelIcon} />
              <ChatRegular className={styles.channelIcon} />
            </div>
            <div className={styles.channelContent}>
              <div className={styles.channelTitle}>{t('sendOutreachDialog.channel.bothTitle')}</div>
              <div className={styles.channelDescription}>
                {t('sendOutreachDialog.channel.bothDescription')}
              </div>
            </div>
          </label>
        </div>
      </RadioGroup>

      <div style={{ marginTop: '16px', padding: '12px', backgroundColor: tokens.colorNeutralBackground2, borderRadius: '8px' }}>
        <div style={{ fontSize: '14px', fontWeight: '600', marginBottom: '8px' }}>
          {t('sendOutreachDialog.channel.selectedCount', { count: selectedClients.length })}
        </div>
        <div style={{ fontSize: '13px', color: tokens.colorNeutralForeground3, display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
          <span>{t('sendOutreachDialog.channel.withEmailCount', { count: clientsWithEmail.length })}</span>
          <span>{t('sendOutreachDialog.channel.withPhoneCount', { count: clientsWithPhone.length })}</span>
          {clientTypeCounts.individuals > 0 && (
            <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <PersonRegular style={{ fontSize: '12px' }} />
              {t('sendOutreachDialog.channel.individualsCount', { count: clientTypeCounts.individuals })}
            </span>
          )}
          {clientTypeCounts.businesses > 0 && (
            <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <BuildingRegular style={{ fontSize: '12px' }} />
              {t('sendOutreachDialog.channel.businessesCount', { count: clientTypeCounts.businesses })}
            </span>
          )}
        </div>
      </div>
    </div>
  );

  const renderComposeStep = () => (
    <div>
      {(channel === 'email' || channel === 'both') && (
        <div style={{ marginBottom: '24px' }}>
          <h4 style={{ marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <MailRegular /> {t('sendOutreachDialog.compose.emailContentHeading')}
          </h4>

          <div className={styles.formField}>
            <label className={styles.fieldLabel}>{t('sendOutreachDialog.compose.templateLabel')}</label>
            {clientTypeCounts.isMixed && (
              <div style={{ fontSize: '12px', color: tokens.colorPaletteYellowForeground1, marginBottom: '8px' }}>
                {t('sendOutreachDialog.compose.mixedClientWarning')}
              </div>
            )}
            <Dropdown
              value={selectedTemplate}
              onOptionSelect={(_, data) => setSelectedTemplate(data.optionValue as string)}
              style={{ width: '100%' }}
            >
              {/* Auto templates - best for mixed audiences */}
              {groupedTemplates.auto.length > 0 && (
                <>
                  <Option key="header-auto" value="" text="" disabled>
                    <div style={{ fontWeight: 600, fontSize: '11px', color: tokens.colorNeutralForeground3, textTransform: 'uppercase' }}>
                      {t('sendOutreachDialog.compose.templateGroups.auto')}
                    </div>
                  </Option>
                  {groupedTemplates.auto.map(tpl => (
                    <Option key={tpl.id} value={tpl.id} text={tpl.name}>
                      <div>
                        <div>{tpl.name}</div>
                        <div style={{ fontSize: '12px', color: tokens.colorNeutralForeground3 }}>
                          {tpl.description}
                        </div>
                      </div>
                    </Option>
                  ))}
                </>
              )}
              {/* All templates - universal */}
              {groupedTemplates.all.length > 0 && (
                <>
                  <Option key="header-all" value="" text="" disabled>
                    <div style={{ fontWeight: 600, fontSize: '11px', color: tokens.colorNeutralForeground3, textTransform: 'uppercase' }}>
                      {t('sendOutreachDialog.compose.templateGroups.universal')}
                    </div>
                  </Option>
                  {groupedTemplates.all.map(tpl => (
                    <Option key={tpl.id} value={tpl.id} text={tpl.name}>
                      <div>
                        <div>{tpl.name}</div>
                        <div style={{ fontSize: '12px', color: tokens.colorNeutralForeground3 }}>
                          {tpl.description}
                        </div>
                      </div>
                    </Option>
                  ))}
                </>
              )}
              {/* Individual templates */}
              {groupedTemplates.individual.length > 0 && (
                <>
                  <Option key="header-individual" value="" text="" disabled>
                    <div style={{ fontWeight: 600, fontSize: '11px', color: tokens.colorNeutralForeground3, textTransform: 'uppercase' }}>
                      {t('sendOutreachDialog.compose.templateGroups.individual')}
                    </div>
                  </Option>
                  {groupedTemplates.individual.map(tpl => (
                    <Option key={tpl.id} value={tpl.id} text={tpl.name}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <PersonRegular style={{ fontSize: '14px', color: tokens.colorNeutralForeground3 }} />
                        <div>
                          <div>{tpl.name}</div>
                          <div style={{ fontSize: '12px', color: tokens.colorNeutralForeground3 }}>
                            {tpl.description}
                          </div>
                        </div>
                      </div>
                    </Option>
                  ))}
                </>
              )}
              {/* Business templates */}
              {groupedTemplates.business.length > 0 && (
                <>
                  <Option key="header-business" value="" text="" disabled>
                    <div style={{ fontWeight: 600, fontSize: '11px', color: tokens.colorNeutralForeground3, textTransform: 'uppercase' }}>
                      {t('sendOutreachDialog.compose.templateGroups.business')}
                    </div>
                  </Option>
                  {groupedTemplates.business.map(tpl => (
                    <Option key={tpl.id} value={tpl.id} text={tpl.name}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <BuildingRegular style={{ fontSize: '14px', color: tokens.colorNeutralForeground3 }} />
                        <div>
                          <div>{tpl.name}</div>
                          <div style={{ fontSize: '12px', color: tokens.colorNeutralForeground3 }}>
                            {tpl.description}
                          </div>
                        </div>
                      </div>
                    </Option>
                  ))}
                </>
              )}
            </Dropdown>
          </div>

          {selectedTemplate === 'custom' && (
            <>
              <div className={styles.formField}>
                <label className={styles.fieldLabel}>{t('sendOutreachDialog.compose.subjectLabel')}</label>
                <Input
                  value={customSubject}
                  onChange={(_, data) => setCustomSubject(data.value)}
                  placeholder={t('sendOutreachDialog.compose.subjectPlaceholder')}
                  style={{ width: '100%' }}
                />
              </div>
              <div className={styles.formField}>
                <label className={styles.fieldLabel}>{t('sendOutreachDialog.compose.bodyLabel')}</label>
                <Textarea
                  value={customBody}
                  onChange={(_, data) => setCustomBody(data.value)}
                  placeholder={t('sendOutreachDialog.compose.bodyPlaceholder')}
                  rows={6}
                  style={{ width: '100%' }}
                />
              </div>
            </>
          )}
        </div>
      )}

      {(channel === 'sms' || channel === 'both') && (
        <div>
          <h4 style={{ marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <ChatRegular /> {t('sendOutreachDialog.compose.smsContentHeading')}
          </h4>

          <div className={styles.formField}>
            <label className={styles.fieldLabel}>{t('sendOutreachDialog.compose.smsMessageLabel', { max: SMS_MAX_LENGTH })}</label>
            <Textarea
              value={smsMessage}
              onChange={(_, data) => setSmsMessage(data.value)}
              placeholder={t('sendOutreachDialog.compose.smsMessagePlaceholder')}
              rows={4}
              style={{ width: '100%' }}
            />
            <div
              className={`${styles.charCount} ${
                smsMessage.length > SMS_MAX_LENGTH ? styles.charCountError :
                smsMessage.length > SMS_WARNING_LENGTH ? styles.charCountWarning : ''
              }`}
            >
              {t('sendOutreachDialog.compose.smsCharCount', { count: smsMessage.length, max: SMS_MAX_LENGTH })}
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const renderPreviewStep = () => (
    <div>
      {selectedClients.length > 1 && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
          <Button
            appearance="subtle"
            icon={<ChevronLeftRegular />}
            disabled={previewIndex === 0}
            onClick={() => setPreviewIndex(i => Math.max(0, i - 1))}
          >
            {t('sendOutreachDialog.preview.previousButton')}
          </Button>
          <span>
            {t('sendOutreachDialog.preview.counter', { index: previewIndex + 1, count: selectedClients.length })}
          </span>
          <Button
            appearance="subtle"
            icon={<ChevronRightRegular />}
            iconPosition="after"
            disabled={previewIndex === selectedClients.length - 1}
            onClick={() => setPreviewIndex(i => Math.min(selectedClients.length - 1, i + 1))}
          >
            {t('sendOutreachDialog.preview.nextButton')}
          </Button>
        </div>
      )}

      {currentPreviewClient && (
        <div className={styles.previewCard}>
          <div className={styles.previewClient}>
            {currentPreviewClient.clientType === 'account' ? <BuildingRegular /> : <PersonRegular />}
            <strong>{currentPreviewClient.name}</strong>
            {currentPreviewClient.email && (
              <Badge appearance="outline" size="small">
                <MailRegular style={{ marginRight: '4px', fontSize: '10px' }} />
                {currentPreviewClient.email}
              </Badge>
            )}
            {currentPreviewClient.phone && (
              <Badge appearance="outline" size="small">
                <PhoneRegular style={{ marginRight: '4px', fontSize: '10px' }} />
                {currentPreviewClient.phone}
              </Badge>
            )}
          </div>

          {(channel === 'email' || channel === 'both') && currentPreviewClient.email && (
            <div style={{ marginTop: '12px' }}>
              <div style={{ fontSize: '12px', fontWeight: '600', marginBottom: '4px' }}>{t('sendOutreachDialog.preview.emailPreviewHeading')}</div>
              <div className={styles.previewContent}>
                <div><strong>{t('sendOutreachDialog.preview.toLabel')}</strong> {currentPreviewClient.email}</div>
                <div><strong>{t('sendOutreachDialog.preview.subjectLabel')}</strong> {customSubject || templates?.find(tpl => tpl.id === selectedTemplate)?.subject.replace('{year}', (taxYear + 1).toString())}</div>
                <hr style={{ margin: '8px 0', border: 'none', borderTop: '1px solid var(--tl-border)' }} />
                <div style={{ fontSize: '13px' }}>
                  {selectedTemplate === 'custom' ? customBody : t('sendOutreachDialog.preview.genericBodyPreview', { name: currentPreviewClient.name })}
                </div>
              </div>
            </div>
          )}

          {(channel === 'sms' || channel === 'both') && currentPreviewClient.phone && (
            <div style={{ marginTop: '12px' }}>
              <div style={{ fontSize: '12px', fontWeight: '600', marginBottom: '4px' }}>{t('sendOutreachDialog.preview.smsPreviewHeading')}</div>
              <div className={styles.previewContent} style={{ maxHeight: '100px' }}>
                <div><strong>{t('sendOutreachDialog.preview.toLabel')}</strong> {currentPreviewClient.phone}</div>
                <hr style={{ margin: '8px 0', border: 'none', borderTop: '1px solid var(--tl-border)' }} />
                <div style={{ fontSize: '13px' }}>{smsMessage}</div>
              </div>
            </div>
          )}
        </div>
      )}

      <div style={{ backgroundColor: tokens.colorNeutralBackground2, padding: '12px', borderRadius: '8px' }}>
        <strong>{t('sendOutreachDialog.preview.readyToSendHeading')}</strong>
        <ul style={{ margin: '8px 0 0 20px', fontSize: '14px' }}>
          {(channel === 'email' || channel === 'both') && (
            <li>{t('sendOutreachDialog.preview.emailsCount', { count: clientsWithEmail.length })}</li>
          )}
          {(channel === 'sms' || channel === 'both') && (
            <li>{t('sendOutreachDialog.preview.smsCount', { count: clientsWithPhone.length })}</li>
          )}
        </ul>
      </div>
    </div>
  );

  const renderSendingStep = () => (
    <div className={styles.sendingProgress}>
      <Spinner size="large" label={t('sendOutreachDialog.sending.label')} />
      <ProgressBar value={sendProgress / 100} style={{ marginTop: '24px' }} />
      <div style={{ marginTop: '8px', color: tokens.colorNeutralForeground3 }}>
        {t('sendOutreachDialog.sending.percentComplete', { percent: sendProgress })}
      </div>
    </div>
  );

  const renderCompleteStep = () => {
    if (!sendResults) return null;

    const totalSuccess = sendResults.emailSuccess + sendResults.smsSuccess;
    const totalFailed = sendResults.emailFailed + sendResults.smsFailed;
    const isAllSuccess = totalFailed === 0;
    const isAllFailed = totalSuccess === 0;

    return (
      <div>
        <div
          className={`${styles.resultSummary} ${
            isAllSuccess ? styles.resultSuccess :
            isAllFailed ? styles.resultError : styles.resultMixed
          }`}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
            {isAllSuccess ? (
              <CheckmarkCircleRegular style={{ color: tokens.colorPaletteGreenForeground1, fontSize: '24px' }} />
            ) : (
              <DismissCircleRegular style={{ color: tokens.colorPaletteRedForeground1, fontSize: '24px' }} />
            )}
            <strong style={{ fontSize: '16px' }}>
              {isAllSuccess ? t('sendOutreachDialog.complete.allSuccess') :
               isAllFailed ? t('sendOutreachDialog.complete.allFailed') :
               t('sendOutreachDialog.complete.partial')}
            </strong>
          </div>

          <div style={{ fontSize: '14px' }}>
            {sendResults.emailSuccess > 0 && (
              <div>{t('sendOutreachDialog.complete.emailsSucceeded', { count: sendResults.emailSuccess })}</div>
            )}
            {sendResults.smsSuccess > 0 && (
              <div>{t('sendOutreachDialog.complete.smsSucceeded', { count: sendResults.smsSuccess })}</div>
            )}
            {sendResults.emailFailed > 0 && (
              <div style={{ color: tokens.colorPaletteRedForeground1 }}>
                {t('sendOutreachDialog.complete.emailsFailed', { count: sendResults.emailFailed })}
              </div>
            )}
            {sendResults.smsFailed > 0 && (
              <div style={{ color: tokens.colorPaletteRedForeground1 }}>
                {t('sendOutreachDialog.complete.smsFailed', { count: sendResults.smsFailed })}
              </div>
            )}
          </div>
        </div>

        {sendResults.errors.length > 0 && (
          <div style={{ marginTop: '16px' }}>
            <div style={{ fontWeight: '600', marginBottom: '8px' }}>{t('sendOutreachDialog.complete.errorsHeading')}</div>
            <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '13px', color: tokens.colorPaletteRedForeground1 }}>
              {sendResults.errors.slice(0, 5).map((err, i) => (
                <li key={i}>{err}</li>
              ))}
              {sendResults.errors.length > 5 && (
                <li>{t('sendOutreachDialog.complete.moreErrors', { count: sendResults.errors.length - 5 })}</li>
              )}
            </ul>
          </div>
        )}
      </div>
    );
  };

  const renderContent = () => {
    switch (step) {
      case 'channel': return renderChannelStep();
      case 'compose': return renderComposeStep();
      case 'preview': return renderPreviewStep();
      case 'sending': return renderSendingStep();
      case 'complete': return renderCompleteStep();
    }
  };

  return (
    <Dialog open={open} onOpenChange={(_, data) => data.open ? null : handleClose()}>
      <DialogSurface style={{ maxWidth: '600px' }}>
        <DialogBody>
          <DialogTitle>{t('sendOutreachDialog.title')}</DialogTitle>
          <DialogContent>
            {step !== 'sending' && step !== 'complete' && renderStepIndicator()}
            {renderContent()}
          </DialogContent>
          <DialogActions>
            {step === 'complete' ? (
              <Button appearance="primary" onClick={handleComplete}>
                {t('sendOutreachDialog.doneButton')}
              </Button>
            ) : step === 'sending' ? null : (
              <>
                {step !== 'channel' && (
                  <Button appearance="secondary" onClick={handleBack}>
                    {t('sendOutreachDialog.backButton')}
                  </Button>
                )}
                <Button appearance="secondary" onClick={handleClose}>
                  {t('common.cancel')}
                </Button>
                <Button
                  appearance="primary"
                  icon={step === 'preview' ? <SendRegular /> : undefined}
                  onClick={handleNext}
                  disabled={!canProceed()}
                >
                  {step === 'preview' ? t('sendOutreachDialog.sendButton') : t('sendOutreachDialog.nextButton')}
                </Button>
              </>
            )}
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}
